import { faEdit, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Stack,
  Table,
} from "react-bootstrap";
import DeleteButton from "../../Helpers/deleteButton";
import { UserDTO } from "../../models/UserDTO";
import UserService from "../../services/user.service";
import { ModalUserForm } from "./ModalNewUser/ModalUserForm";

type Notification = {
  show: boolean;
  message: string;
  title: string;
  variant: "success" | "danger";
};

export const Users = () => {
  const [users, setUsers] = useState<UserDTO[]>([]);
  const [modalShow, setModalShow] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDTO | null>(null);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [filterText, setFilterText] = useState("");

  const loadUsers = async () => {
    try {
      const data = await UserService.getUsers();
      setUsers(data);
    } catch (error) {
      console.error("Failed to load users:", error);
      setNotification({
        show: true,
        variant: "danger",
        title: "Error de Carga",
        message: "No se pudieron cargar los usuarios.",
      });
    }
  };

  const handleSave = async (user: UserDTO) => {
    try {
      if (user.id) {
        const response = await UserService.updateUser(user);
        if (response.ok) {
          setNotification({
            show: true,
            variant: "success",
            title: "Éxito",
            message: "Actualizado correctamente.",
          });
          setSelectedUser(null);
          setModalShow(false);
          await loadUsers();
          return;
        } else {
          const errorMessage = await response.text();
          throw new Error("Error al actualizar usuario: " + errorMessage);
        }
      } else {
        const response = await UserService.addUser(user);

        if (response.ok) {
          setNotification({
            show: true,
            variant: "success",
            title: "Éxito",
            message: "Guardado correctamente.",
          });
          setSelectedUser(null);
          setModalShow(false);
          await loadUsers();
          return;
        } else {
          const errorMessage = await response.text();
          throw new Error("Error al agregar usuario: " + errorMessage);
        }
      }
    } catch (error) {
      console.error("Failed to save user:", error);
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: error instanceof Error ? error.message : "",
      });
    }
  };

  const onDelete = async (id: number) => {
    try {
      const response = await UserService.deleteUser(id);
      if (response.ok) {
        setNotification({
          show: true,
          variant: "success",
          title: "Éxito",
          message: "Usuario eliminado correctamente.",
        });
        await loadUsers();
      } else {
        const errorMessage = await response.text();
        throw new Error("Error al eliminar usuario: " + errorMessage);
      }
    } catch (error) {
      console.error("Failed to delete user:", error);
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: error instanceof Error ? error.message : "",
      });
    }
  };

  const getFilteredUsers = () => {
    const search = filterText.toLowerCase();

    return users.filter((u) => {
      return (
        u.userName?.toLowerCase().includes(search) ||
        u.name?.toLowerCase().includes(search) ||
        u.email?.toLowerCase().includes(search) ||
        (Array.isArray(u.roles) &&
          u.roles.some((r) => r.roleName?.toLowerCase().includes(search)))
      );
    });
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <>
      <div className="notification-container">
        {notification && (
          <Alert
            variant={notification.variant}
            onClose={() => setNotification(null)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{notification.title}</Alert.Heading>
            {notification.message}
          </Alert>
        )}
      </div>

      <Container className="pt-3">
        <Card>
          <Card.Body>
            <Card.Title>Usuarios</Card.Title>
            <hr />
            <Row className="mb-3 g-3 align-items-center">
              <Col md={5}>
                <Form.Control
                  type="text"
                  placeholder="Buscar..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </Col>
              <Col
                md={7}
                className="d-flex justify-content-md-end align-items-center"
              >
                <Stack direction="horizontal" gap={3}>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setSelectedUser(null);
                      setModalShow(true);
                    }}
                    className="d-flex  align-items-center"
                  >
                    <FontAwesomeIcon icon={faPlus} className="white" />
                    <span className="ms-2">Nuevo usuario</span>
                  </Button>
                </Stack>
              </Col>
            </Row>

            <Table striped bordered hover responsive>
              <thead>
                <tr className="text-center">
                  <th>Id</th>
                  <th>Usuario</th>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredUsers().map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.userName}</td>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>
                      {Array.isArray(u.roles) && u.roles.length > 0
                        ? u.roles.map((r) => r.roleName).join(", ")
                        : "Sin rol"}
                    </td>
                    <td className="text-center">
                      <Stack
                        direction="horizontal"
                        gap={2}
                        className="justify-content-center"
                      >
                        <Button
                          variant="primary"
                          size="sm"
                          active
                          title="Editar"
                          onClick={() => {
                            setSelectedUser(u);
                            setModalShow(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} className="white" />
                        </Button>
                        <DeleteButton
                          onClick={() => {
                            onDelete(u.id!);
                          }}
                        />
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
        <ModalUserForm
          show={modalShow}
          onHide={() => setModalShow(false)}
          onSave={handleSave}
          user={selectedUser}
          title={selectedUser ? "Editar usuario" : "Nuevo usuario"}
        />
      </Container>
    </>
  );
};
